import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { ChannelTabListResponse } from "@bezoned/shared/types/api/channel/channel-tab-list.types.js";
import {
  Caption1,
  MenuDivider,
  MenuList,
  Subtitle2,
} from "@fluentui/react-components";
import { Info20Regular, WarningColor } from "@fluentui/react-icons";

import { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import { ContextMenuItem } from "./context-menu-item.jsx";
import { ContextMenuLoader } from "./context-menu-loader.jsx";
import { ContextMenuTabList } from "./context-menu-tab-list.js";

interface ContextMenuContentProps {
  displayName: string | null;
  channelUuid: string | null;
  msChannelId: string | null;
  onNavigationCompleted: () => void;
  onAddNewBoard: () => void;
  onTableSettings?: () => void;
  tabs: ChannelTabListResponse["tabs"] | null;
  loading: boolean;
  variant: "meeting" | "table";
}

export const ContextMenuContent = ({
  displayName,
  channelUuid,
  msChannelId,
  onNavigationCompleted,
  onAddNewBoard,
  onTableSettings,
  tabs,
  loading,
  variant,
}: ContextMenuContentProps) => {
  const isMeetingVariant = variant === "meeting";

  const { navigateToChannelTab, navigateToChannel, isLoading } =
    useTeamsNavigation({
      onCompleted: onNavigationCompleted,
    });

  const handleAddNewBoardClick = () => {
    // This ensures we close the context window when the modal is shown
    onNavigationCompleted();
    onAddNewBoard();
  };

  const handleClick139 = useStableCallback(
    () =>
      msChannelId &&
      navigateToChannel(msChannelId, displayName ?? "conversations")
  );

  const handleClick156 = useStableCallback(() => {
    if (!onTableSettings) {
      return;
    }

    onNavigationCompleted();
    onTableSettings();
  });

  if (loading) {
    return <ContextMenuLoader loading={loading} />;
  }

  if (channelUuid === null) {
    return (
      <section className="flex flex-col gap-2">
        <Subtitle2>
          <WarningColor /> An issue occurred
        </Subtitle2>
        <p>Connection to Teams Channel failed.</p>
      </section>
    );
  }

  return (
    <section
      className={
        isMeetingVariant
          ? "max-h-screen min-h-screen overflow-y-auto"
          : undefined
      }
    >
      <div
        className={
          isMeetingVariant
            ? "flex min-h-screen flex-col justify-between"
            : "flex h-full flex-col justify-between"
        }
      >
        <div>
          {isMeetingVariant && (
            <section className="flex h-28 w-full items-center border-b-2 px-12 shadow">
              {Boolean(displayName) && (
                <h5 className="mb-2 text-2xl font-semibold text-[#696EB1]">
                  {displayName}
                </h5>
              )}
              <img
                alt="BZD Logo"
                className="ml-auto"
                height={48}
                src="/images/bezoned-logo-mark.svg"
                width={48}
              />
            </section>
          )}

          <section className={isMeetingVariant ? "px-12 pt-8" : "border-b p-3"}>
            {displayName && !isMeetingVariant && (
              <h5 className="text-bzd-gray-200 mb-2 font-semibold">
                {displayName}
              </h5>
            )}

            <MenuList
              className={
                isMeetingVariant
                  ? "flex flex-col gap-4"
                  : "text-bzd-gray-400 flex flex-col gap-1"
              }
              hasIcons={true}
            >
              <ContextMenuTabList
                channelUuid={channelUuid}
                displayName={displayName}
                isLoading={isLoading}
                msChannelId={msChannelId}
                navigateToChannel={navigateToChannel}
                navigateToChannelTab={navigateToChannelTab}
                tabs={tabs}
              />

              <MenuDivider />

              <ContextMenuItem
                label="Add tool or board"
                loading={false}
                onClick={handleAddNewBoardClick}
                type="add"
              />

              <ContextMenuItem
                label="Teams channel"
                loading={isLoading(msChannelId ?? "")}
                onClick={handleClick139}
                type="channel"
              />

              {!isMeetingVariant && onTableSettings && (
                <>
                  <MenuDivider />

                  <ContextMenuItem
                    label="Table settings"
                    loading={false}
                    onClick={handleClick156}
                    type="settings"
                  />
                </>
              )}
            </MenuList>
          </section>
        </div>

        {isMeetingVariant && (
          <div className="flex flex-row gap-2 px-12 pb-12">
            <Info20Regular className="size-4" />
            <Caption1>
              Material and information in the room is available for everyone in
              the office.
            </Caption1>
          </div>
        )}
      </div>
    </section>
  );
};
